import WebViewerClass, { Core, WebViewerInstance } from '@pdftron/webviewer';
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTimeoutFn } from 'react-use';
import ProjectContext from '../contexts/ProjectContext';
import { getDownloadUrl } from '../utils/api/upload';
import xfdfApi from '../utils/api/xfdf';
import { AnnotationApi, useAnnotationApi } from './annotationApi';
import { useEnvConfig } from './envConfig';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import WebViewer from '@pdftron/webviewer';

type PdfEditorProps = {
  upload: {
    id: number;
    guid: string;
    canEdit: boolean;
    canDownload?: boolean;
    disableDownload?: boolean;
    tenantId?: number;
  };
  ref: MutableRefObject<HTMLDivElement | null>;
  onClose: Function;
};

export type EditorStatus = 'loading' | 'saving' | 'loaded' | 'saved';
const priviledgedUserRoles = ['Admin', 'Super Admin', 'Divisions Admin', 'Client Manager'];
/**
 * @param param0
 * @returns
 */

export function usePdfEditor({ upload, ref, onClose }: PdfEditorProps): {
  status: EditorStatus;
} {
  const projectId = useProjectId();
  const annotationApi = useAnnotationApi(upload.id, upload.tenantId, projectId);
  const licenseKey = useEnvConfig('WEBVIEWER_KEY');
  const publicUrl = useEnvConfig('PUBLIC_URL');
  const [viewer, setViewer] = useState<null | WebViewerInstance>(null);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);
  const [editorStatus, setEditorStatus] = useState<EditorStatus>('loading');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMacOS = /Macintosh/.test(navigator.userAgent);
  const shouldUseIframe = !(isIOS || isMacOS);
  const settleEditorStatus = useTimeoutFn(() => {
    if (viewer) {
      setEditorStatus('loading');
    }
  }, 1000);

  // Track annotations that existed from previous sessions
  const previousSessionAnnotationIds = useRef<Set<string>>(new Set());

  // True once remote annotations have been imported into the viewer, so the
  // effect below never imports a second time (or imports an empty snapshot
  // when the viewer's annotationsLoaded event wins the race against the
  // GET /api/xfdf fetch).
  const hasImportedRef = useRef(false);

  const handleDownload = async (viewerInstance: WebViewerInstance | null) => {
    await annotationApi.index();
    const { annotationManager } = viewerInstance!.Core;
    annotationManager.getAnnotationsList().forEach((annot) => {
      annot.Locked = true;
      annot.ReadOnly = true;
      annot.LockedContents = true;
      annotationManager.redrawAnnotation(annot);
    });
    viewerInstance!.UI.downloadPdf({
      filename: uuidv4(),
      flags: viewerInstance!.Core.SaveOptions.LINEARIZED,
      downloadType: 'pdf',
      useDisplayAuthor: true,
    });
    setEditorStatus('saved');
    settleEditorStatus[2]();

    return loadRemoteAnnotations(
      annotationApi,
      viewerInstance!,
      previousSessionAnnotationIds,
      false,
      projectId,
    );
  };

  useEffect(() => {
    if (ref.current && !viewer && licenseKey) {
      const element = ref.current;

      const viewerOptions = {
        path: `${publicUrl}/libs/Webviewer`,
        licenseKey,
        extension: 'pdf',
        isAdminUser: true,
        annotationUser: annotationApi.authorName,
        isReadOnly: !upload.canEdit,
        preloadWorker: WebViewer.WorkerTypes.PDF,
        backendType: isIOS
          ? WebViewer.BackendTypes.ASM
          : WebViewer.BackendTypes.WASM,
      };

      const initViewer = shouldUseIframe
        ? WebViewerClass.Iframe
        : WebViewerClass;

      initViewer(viewerOptions, element).then((viewerInstance) => {
        setViewer(viewerInstance);
        const { annotationManager, documentViewer } = viewerInstance.Core;

        viewerInstance.UI.disableElements([
          //as per current requirement, removing Forms tab from webviewer temporarily.
          'formFieldEditButton',
          'formFieldEditPanel',
          'toolbarGroup-Forms',
          'printButton',
          'saveAsButton',
          'downloadButton'
        ]);

        // Hard-enforce view-only when editing is not allowed (e.g. Map / Map+IT / Inspection
        // Tracking projects, where upload.canEdit is already false via hasDocEditing). The
        // isReadOnly constructor option alone does not reliably hide the annotation tools.
        if (!upload.canEdit) {
          annotationManager.enableReadOnlyMode();
          viewerInstance.UI.disableElements([
            'toolbarGroup-Annotate',
            'toolbarGroup-Shapes',
            'toolbarGroup-Insert',
            'toolbarGroup-Edit',
            'toolbarGroup-FillAndSign',
            'toolbarGroup-Measure',
            'toolsHeader',
            'notesPanel',
            'toggleNotesButton',
          ]);
        }

        documentViewer.loadDocument(getDownloadUrl(upload.guid, null, true));

        documentViewer.addEventListener('documentLoaded', () => {
          setEditorStatus('loaded');
        });

        if (upload.disableDownload) {
          const { disableElements } = viewerInstance.UI;
          disableElements(['downloadButton']);
        } else if (upload.canDownload) {
          documentViewer.addEventListener('annotationsLoaded', async () => {
            // This handler imports annotations itself; stop the effect below
            // from importing the same set a second time.
            hasImportedRef.current = true;
            setAnnotationsLoaded(true);
            const annotatations = await annotationApi.index();
            const data = annotatations;
            const annotationCommands = data.filter((d) => d.isCommand);
            
            // Wait for all annotations to be imported and drawn before downloading
            await Promise.all(
              annotationCommands.map(async (row) => {
                const annotations =
                  await annotationManager.importAnnotationCommand(row.xfdfValue!);
                
                // Parse XFDF to extract creation dates
                const parser = new DOMParser();
                const xfdfDoc = parser.parseFromString(row.xfdfValue!, 'application/xml');
                
                annotations.forEach((annot: any) => {
                  // Find the corresponding element in XFDF by matching the annotation name/id
                  const annotElements = xfdfDoc.querySelectorAll('[name], [id]');
                  let creationDateStr = null;
                  
                  for (let i = 0; i < annotElements.length; i++) {
                    const element = annotElements[i];
                    const elementName = element.getAttribute('name') || element.getAttribute('id');
                    
                    if (elementName === annot.Id) {
                      creationDateStr = element.getAttribute('creationdate');
                      break;
                    }
                  }
                  
                  // Parse the PDF date format (D:YYYYMMDDHHmmSS+HH'mm')
                  if (creationDateStr) {
                    const dateMatch = creationDateStr.match(/D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
                    if (dateMatch) {
                      const [, year, month, day, hour, minute, second] = dateMatch;
                      const creationDate = new Date(
                        parseInt(year),
                        parseInt(month) - 1, // Month is 0-indexed
                        parseInt(day),
                        parseInt(hour),
                        parseInt(minute),
                        parseInt(second)
                      );
                      
                      // Set both DateCreated and DateModified to the creation date
                      annot.DateCreated = creationDate;
                      annot.DateModified = creationDate;
                      
                      // Override the date property to use the creation date
                      Object.defineProperty(annot, 'date', {
                        get: function() { return creationDate; },
                        configurable: true
                      });
                    }
                  }
                });
                
                await annotationManager.drawAnnotationsFromList(annotations);
              })
            );
            
            const options = {
              filename: uuidv4(),
              flags: viewerInstance.Core.SaveOptions.LINEARIZED,
              downloadType: 'pdf',
              useDisplayAuthor: true,
            };

            viewerInstance.UI.downloadPdf(options).then(() => {
              toast('Download Completed');
              setTimeout(() => {
                onClose();
              }, 100);
            });
          });
        } else {
          annotationManager.addEventListener('fieldChanged', async () => {
            const xfdf = await annotationManager.exportAnnotationCommand();
            const mainStr = xfdf.replace(/\n/g, '');
            setEditorStatus('saving');

            await annotationApi.save({
              isCommand: true,
              key: uuidv4(),
              xfdfValue: mainStr,
              projectId,
            });

            setEditorStatus('saved');
            settleEditorStatus[2]();
          });

          annotationManager.addEventListener(
            'annotationChanged',
            (annotations, action, info) => {
              onAnnotationChange(
                viewerInstance,
                async (model) => {
                  setEditorStatus('saving');

                  await annotationApi.save(model);

                  setEditorStatus('saved');
                  settleEditorStatus[2]();
                },
                annotations,
                action,
                info,
                projectId,
                annotationApi,
                previousSessionAnnotationIds,
              );
            },
          );

          documentViewer.addEventListener('annotationsLoaded', async () => {
            // Fetch annotations here instead of relying on annotationApi.data:
            // the mount-effect fetch races this event, and when the event wins
            // the state is still [] and annotations never appear (SW2-2188).
            const loadingToastId = toast('Loading annotations…', {
              autoClose: false,
            });

            try {
              let freshData;

              try {
                freshData = await annotationApi.index();
              } catch {
                // one retry before surfacing the failure
                freshData = await annotationApi.index();
              }

              // `annotationApi` here is the first render's object, so its
              // isAppendOnly is still the initial false; importing with it
              // would leave an append-only tenant's annotations editable, and
              // hasImportedRef stops any later import from correcting that.
              const isAppendOnly = annotationApi.resolveIsAppendOnly
                ? await annotationApi.resolveIsAppendOnly()
                : annotationApi.isAppendOnly;

              await loadRemoteAnnotations(
                { ...annotationApi, data: freshData, isAppendOnly },
                viewerInstance,
                previousSessionAnnotationIds,
                upload.canDownload,
                projectId,
              );
              hasImportedRef.current = true;
              setAnnotationsLoaded(true);
              setEditorStatus('loaded');
            } catch {
              // Leave hasImportedRef unset — a silent failure would look
              // identical to the missing-annotations bug.
              toast.error('Annotations failed to load — reload to retry');
            } finally {
              toast.dismiss(loadingToastId);
            }
          });
        }
      });
    }
  }, [licenseKey, publicUrl, isIOS]);

  useEffect(() => {
    if (annotationsLoaded && viewer) {
      // Safety net only: both annotationsLoaded handlers import annotations
      // themselves and set hasImportedRef before this effect runs.
      if (!hasImportedRef.current) {
        hasImportedRef.current = true;
        loadRemoteAnnotations(
          annotationApi,
          viewer,
          previousSessionAnnotationIds,
          upload.canDownload,
          projectId,
        ).then(() => {
          setEditorStatus('loaded');
        });
      }
      if (annotationApi.isAppendOnly) {
        const newDownloadButton = {
          dataElement: 'newDownloadButton',
          label: 'Download',
          onClick: () => handleDownload(viewer),
          icon: 'icon-download',
        };

        const mainMenuFlyout = viewer.UI.Flyouts.getFlyout('MainMenuFlyout');
        const mainMenuFlyoutItems = mainMenuFlyout.items;
        const downloadButtonIndex = mainMenuFlyoutItems.findIndex(
          (item: any) => item.dataElement === 'downloadButton',
        );

        // -1 after the first swap has already replaced the button; skip so
        // re-runs (isAppendOnly arrives async) stay idempotent.
        if (downloadButtonIndex !== -1) {
          mainMenuFlyoutItems[downloadButtonIndex] = newDownloadButton;
          mainMenuFlyout.setItems(mainMenuFlyoutItems);
        }
      }
    }
    // isAppendOnly is fetched async (tenant API) and can resolve after
    // annotationsLoaded — without it in deps the flyout swap never happens.
  }, [annotationsLoaded, viewer, annotationApi.isAppendOnly]);

  return { status: editorStatus };
}

export async function loadRemoteAnnotations(
  { data, authorRole, isAppendOnly }: AnnotationApi,
  webViewerInstance: WebViewerInstance,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
  isDownloadMode: boolean = false,
  /**
   * The project the document is being viewed from, when it is being viewed
   * from one at all. Undefined on the master Regulation Group page.
   */
  projectId?: number,
) {
  const isPrivileged =
    priviledgedUserRoles.includes(authorRole) || !isAppendOnly;
  const annotationCommands = data.filter((d) => d.isCommand);

  await Promise.all(
    annotationCommands.map(async (row) => {
      // A row with no projectId belongs to the master document. Inside a
      // project those annotations are INHERITED and must stay read-only:
      // the project layer may add its own, never change master's. Editing
      // one used to rewrite the master row's project_id, moving the
      // annotation out of the master document and out of every other
      // project into the one that touched it (SW2-2067).
      const isInheritedFromMaster = Boolean(projectId) && !row.projectId;
      const readOnly =
        !isPrivileged || (isAppendOnly && isDownloadMode) || isInheritedFromMaster;

      const annotations =
        await webViewerInstance.Core.annotationManager.importAnnotationCommand(
          row.xfdfValue!,
        );
      annotations.forEach((annot) => {
        annot.Locked = readOnly;
        annot.ReadOnly = readOnly;
        annot.LockedContents = readOnly;

        // Track this annotation as existing from a previous session
        if (previousSessionAnnotationIds && annot.Id) {
          previousSessionAnnotationIds.current.add(annot.Id);
        }
      });
      await webViewerInstance.Core.annotationManager.drawAnnotationsFromList(
        annotations,
      );
    }),
  );
}

async function onAnnotationChange(
  webViewerInstance: WebViewerInstance,
  apiSave: AnnotationApi['save'],
  annotations: Core.Annotations.Annotation[],
  action: string,
  info: Core.AnnotationManager.AnnotationChangedInfoObject,
  projectId?: number,
  annotationApi?: AnnotationApi,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
) {
  if (!info.imported) {
    await logAnnotationModification(
      annotations,
      action,
      annotationApi,
      previousSessionAnnotationIds,
      projectId,
    );

    await saveAnnotationCommands(
      webViewerInstance.Core.annotationManager,
      apiSave,
      projectId,
    );
  }
}

async function logAnnotationModification(
  annotations: Core.Annotations.Annotation[],
  action: string,
  annotationApi?: AnnotationApi,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
  projectId?: number,
) {
  const existingAnnotations = annotations.filter(
    (a) =>
      previousSessionAnnotationIds &&
      previousSessionAnnotationIds.current.has(a.Id),
  );

  const saveRequests: any[] = [];

  existingAnnotations.forEach((annot) => {
    saveRequests.push(
      annotationApi?.save({
        isModificationLog: true,
        action: action,
        key: annot.Id || '',
        xfdfValue: '',
        projectId,
      }),
    );
  });
}

async function saveAnnotationCommands(
  manager: Core.AnnotationManager,
  apiSave: AnnotationApi['save'],
  projectId?: number,
) {
  const xfdfString = await manager.exportAnnotationCommand();
  const parser = new DOMParser();
  const commandData = parser.parseFromString(xfdfString, 'application/xml');
  const addedAnnots = commandData.getElementsByTagName('add')[0];
  const modifiedAnnots = commandData.getElementsByTagName('modify')[0];
  const deletedAnnots = commandData.getElementsByTagName('delete')[0];
  const saveRequests: Promise<void>[] = [];

  // List of added annotations
  addedAnnots.childNodes.forEach((child) => {
    if (isElement(child) && hasTitleAttribute(child)) {
      const model = xfdfApi.getCommandModel(child, 'add', projectId);

      if (model.key) {
        saveRequests.push(apiSave(model));
      }
    }
  });

  // List of modified annotations
  modifiedAnnots.childNodes.forEach((child) => {
    if (isElement(child) && hasTitleAttribute(child)) {
      saveRequests.push(
        apiSave(xfdfApi.getCommandModel(child, 'modify', projectId)),
      );
    }
  });

  // List of deleted annotations
  deletedAnnots.childNodes.forEach((child) => {
    if (isElement(child)) {
      saveRequests.push(
        apiSave(xfdfApi.getCommandModel(child, 'delete', projectId)),
      );
    }
  });

  await Promise.all(saveRequests);
}

function isElement(child: ChildNode): child is Element {
  return child.nodeType == Node.ELEMENT_NODE;
}

function hasTitleAttribute(element: Element) {
  return (element.getAttribute('title') || '').length > 0;
}

function useProjectId() {
  const { project } = useContext(ProjectContext);

  if (project && project.id) {
    return project.id;
  }

  return undefined;
}
